import React, { useState, useRef, useCallback } from 'react';

/* ─── Image resolver: uses DB `image` field + env prefix ─── */
export const getModuleImage = (product, envOverride) => {
  const img = product.image || product._image || '';
  if (!img) return '/images/configurator/qr.png';
  if (img === 'qr') return '/images/configurator/qr.png';
  const env = envOverride || 'interior';
  const prefix = env === 'exterior' ? 'exterior' : 'interior';
  return `/images/configurator/${prefix}_${img}.svg`;
};

export const getModuleWidth = (product, env) => {
  // Accept either a product object or a plain name string (back-compat)
  const prod = (product && typeof product === 'object') ? product : { name: product };
  // QR point of sale has no physical footprint — don't sum it into installation width
  if (prod.image === 'qr' || prod.code === 'POS_QR') return 0;
  // Use explicit width from DB if available
  if (env === 'exterior' && prod.widthExterior != null) return prod.widthExterior;
  if (env !== 'exterior' && prod.widthInterior != null) return prod.widthInterior;
  // Fallback: derive from product name (legacy)
  const n = (prod.name || '').toUpperCase();
  if (n.match(/LK[\s-]*04/) && n.match(/X+L/)) return env === 'exterior' ? 128 : 125.7;
  if (n.match(/LK[\s-]*06/) && n.match(/XL/))  return env === 'exterior' ? 88  : 85.7;
  return env === 'exterior' ? 68 : 65.7;
};

/* ─── Unique ID helper ─── */
let _uid = 0;
const uid = () => `vll-${Date.now()}-${++_uid}`;

/* ─── Shared dimension style pieces ─── */
const S = {
  arrowL: { width: 0, height: 0, borderTop: '5px solid transparent', borderBottom: '5px solid transparent', borderRight: '8px solid #888', flexShrink: 0 },
  arrowR: { width: 0, height: 0, borderTop: '5px solid transparent', borderBottom: '5px solid transparent', borderLeft:  '8px solid #888', flexShrink: 0 },
  arrowU: { width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: '8px solid #888', flexShrink: 0 },
  arrowD: { width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop:    '8px solid #888', flexShrink: 0 },
  lineH:  { flex: 1, height: 1, background: '#888', display: 'inline-block' },
  lineV:  { flex: 1, width:  1, background: '#888', display: 'inline-block' },
};

/* ─── Per-door dimensions lookup (matches website) ─── */
const DIMS = {
  LK12:  '30×30×50 cm', LK12C: '30×30×50 cm',
  LK10:  '30×30 / 60×30 cm',
  LK08:  '30×30×50 cm',
  LK06:  '60×30×50 cm', LK06XL: '60×40×50 cm',
  LK04:  '90×30×50 cm', LK04XL: '90×60×50 cm',
  LK02:  '180×30×50 cm',
};
const getDims = (code) => DIMS[code] || '';

/* ═══════════════════════════════════════════════════
   VisualLockerLine — Controlled component
   Props:
     products      – DB products (cabinet + pos)
     env           – 'interior' | 'exterior' | 'pos'
     lockedEnv     – 'interior' | 'exterior' | null
     configLine    – array of placed modules (parent-managed)
     onChange(line) – called on every line change
   ═══════════════════════════════════════════════════ */
export default function VisualLockerLine({ products = [], env = 'interior', lockedEnv = null, configLine = [], onChange }) {
  const [draggedCatalog, setDraggedCatalog] = useState(null);
  const [draggedIndex,   setDraggedIndex]   = useState(null);
  const [dragOverIndex,  setDragOverIndex]  = useState(null);
  const dropZoneRef = useRef(null);

  const push = (newLine) => { if (onChange) onChange(newLine); };

  // Determine which env tab maps to indoor/outdoor for filtering
  const envFilter = env === 'exterior' ? 'outdoor' : env === 'pos' ? 'pos' : 'indoor';

  // POS QR vs other POS are mutually exclusive
  const hasQRInLine    = configLine.some(m => m.category === 'pos' && (m.code === 'POS_QR' || m.image === 'qr'));
  const hasNonQRInLine = configLine.some(m => m.category === 'pos' && m.code !== 'POS_QR' && m.image !== 'qr');
  const isQRProduct    = (p) => p && p.category === 'pos' && (p.code === 'POS_QR' || p.image === 'qr');

  /* ─── Catalog sidebar ─── */
  const catalog = products.filter(p => {
    if (!p) return false;
    if (env === 'pos') return p.category === 'pos';
    if (p.category !== 'cabinet') return false;
    const pEnv = p.env || 'both';
    if (envFilter === 'outdoor') return pEnv === 'outdoor' || pEnv === 'both';
    return pEnv === 'indoor' || pEnv === 'both';
  }).map(p => {
    let disabled = false;
    // In POS tab, mark opposite-env items as disabled when cabinets lock an env
    if (env === 'pos' && lockedEnv) {
      const pEnv = p.env || 'both';
      if (pEnv !== 'both') {
        const locked = lockedEnv === 'exterior' ? 'indoor' : 'outdoor';
        if (pEnv === locked) disabled = true;
      }
    }
    // QR ↔ other POS exclusivity
    if (p.category === 'pos') {
      if (hasQRInLine && !isQRProduct(p)) disabled = true;
      if (hasNonQRInLine && isQRProduct(p)) disabled = true;
    }
    return disabled ? { ...p, _disabled: true } : p;
  });

  const addModule = (prod) => {
    if (prod._disabled) return;
    // Enforce QR / non-QR exclusivity on add
    if (prod.category === 'pos') {
      if (hasQRInLine && !isQRProduct(prod)) return;
      if (hasNonQRInLine && isQRProduct(prod)) return;
    }
    push([...configLine, { ...prod, uid: uid(), _imgWidth: getModuleWidth(prod, env) }]);
  };

  /* ─── Drag from catalog ─── */
  const onCatalogDragStart = (e, prod) => {
    setDraggedCatalog(prod);
    e.dataTransfer.effectAllowed = 'copy';
  };

  /* ─── Drop ─── */
  const onDrop = useCallback((e) => {
    e.preventDefault();
    if (draggedCatalog) {
      // Enforce QR / non-QR exclusivity on drop
      if (draggedCatalog.category === 'pos') {
        if (hasQRInLine && !isQRProduct(draggedCatalog))   { setDraggedCatalog(null); setDragOverIndex(null); return; }
        if (hasNonQRInLine && isQRProduct(draggedCatalog)) { setDraggedCatalog(null); setDragOverIndex(null); return; }
      }
      const idx = dragOverIndex != null ? dragOverIndex : configLine.length;
      const next = [...configLine];
      next.splice(idx, 0, { ...draggedCatalog, uid: uid(), _imgWidth: getModuleWidth(draggedCatalog, env) });
      push(next);
      setDraggedCatalog(null);
      setDragOverIndex(null);
      return;
    }
    if (draggedIndex != null && dragOverIndex != null && draggedIndex !== dragOverIndex) {
      const next = [...configLine];
      const [moved] = next.splice(draggedIndex, 1);
      next.splice(dragOverIndex > draggedIndex ? dragOverIndex - 1 : dragOverIndex, 0, moved);
      push(next);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggedCatalog, draggedIndex, dragOverIndex, configLine, env]);

  /* ─── Reorder within line ─── */
  const onLineDragStart = (e, idx) => { setDraggedIndex(idx); e.dataTransfer.effectAllowed = 'move'; };
  const onLineDragOver  = (e, idx) => { e.preventDefault(); setDragOverIndex(idx); };
  const onLineDragEnd   = ()       => { setDraggedIndex(null); setDragOverIndex(null); };
  const removeModule    = (uid)    => push(configLine.filter(m => m.uid !== uid));

  /* ─── Totals ─── */
  const totalLockers = configLine.reduce((s, m) => s + (m.lockers || 0), 0);
  const totalCm      = configLine.reduce((s, m) => s + (m._imgWidth ?? 65.7), 0);
  const totalDisplay = totalCm >= 100
    ? `${(totalCm / 100).toFixed(3).replace(/\.?0+$/, '')} m`
    : `${parseFloat(totalCm.toFixed(2))} cm`;
  const height = env === 'exterior' ? '1.94 m' : '2.035 m';

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>

      {/* ══ Drop zone ══ */}
      <div style={{ flex: 1, minWidth: 0 }}>

        {/* Width dimension line */}
        {configLine.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', padding: '6px 0 2px 12px', marginRight: 40 }}>
            <span style={S.arrowL} />
            <span style={S.lineH} />
            <span style={{ padding: '0 10px', fontSize: 11, fontWeight: 700, color: '#444', whiteSpace: 'nowrap', fontFamily: 'Courier New, monospace' }}>
              {totalDisplay} — {totalLockers} taquillas
            </span>
            <span style={S.lineH} />
            <span style={S.arrowR} />
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          {/* Drop zone */}
          <div
            ref={dropZoneRef}
            style={{
              flex: 1,
              minHeight: 260,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              gap: 0,
              background: 'linear-gradient(180deg, #f8fbff 0%, #edf3fa 100%)',
              border: `2.5px dashed ${draggedCatalog ? '#0b97ff' : '#d0d8e2'}`,
              borderRadius: 10,
              padding: configLine.length === 0 ? '40px 24px' : '8px 24px 0 24px',
              position: 'relative',
              overflowX: 'auto',
              overflowY: 'hidden',
              cursor: 'copy',
              transition: 'border-color 0.2s, background 0.2s',
              boxShadow: draggedCatalog ? 'inset 0 0 24px rgba(11,151,255,0.06)' : 'none',
            }}
            onDrop={onDrop}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = draggedCatalog ? 'copy' : 'move'; }}
            onDragLeave={() => setDragOverIndex(null)}
          >
            {/* Empty placeholder */}
            {configLine.length === 0 && (
              <div style={{ textAlign: 'center', color: '#b0b8c4', userSelect: 'none', pointerEvents: 'none' }}>
                <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"
                  style={{ opacity: 0.35, display: 'block', margin: '0 auto 10px' }}>
                  <rect x="2" y="3" width="5" height="18" rx="1"/>
                  <rect x="7" y="3" width="5" height="18" rx="0"/>
                  <rect x="12" y="3" width="5" height="18" rx="0"/>
                  <rect x="17" y="3" width="5" height="18" rx="1"/>
                </svg>
                <div style={{ fontSize: 14, fontWeight: 600 }}>Arrastra modulos aqui</div>
                <div style={{ fontSize: 12, color: '#c8cdd4', marginTop: 4 }}>o haz clic en "+" del panel lateral</div>
              </div>
            )}

            {/* Floor line */}
            {configLine.length > 0 && (
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 4,
                background: 'linear-gradient(90deg, #c4cdd8, #a0adb8, #c4cdd8)', borderRadius: '2px 2px 0 0', zIndex: 1 }} />
            )}

            {/* Placed modules */}
            {configLine.map((m, idx) => {
              // Short tag: extract first code like LK12, LK06XL, VLK08, QR
              const tag = (m.code || m.name || '').replace(/^(POS_)?(INT_|EXT_)?/, '').split(/[\s-]/)[0];
              return (
              <div
                key={m.uid}
                draggable
                onDragStart={(e) => onLineDragStart(e, idx)}
                onDragOver={(e)  => onLineDragOver(e, idx)}
                onDragEnd={onLineDragEnd}
                style={{
                  display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                  position: 'relative', flexShrink: 0, cursor: 'grab', zIndex: 2,
                  margin: 0, padding: 0, lineHeight: 0, verticalAlign: 'bottom',
                  opacity: draggedIndex === idx ? 0.25 : 1,
                  transition: 'opacity 0.15s, transform 0.15s',
                }}
                className="vll-module"
              >
                {/* Insert indicator */}
                {dragOverIndex === idx && (
                  <div style={{ position: 'absolute', left: -2, top: '8%', bottom: '8%',
                    width: 3, background: '#ffc905', borderRadius: 3, zIndex: 10 }} />
                )}

                <img
                  src={getModuleImage(m, env === 'pos'
                    ? (m.category === 'pos' ? (m.env === 'outdoor' ? 'exterior' : 'interior') : (lockedEnv || 'interior'))
                    : env)}
                  alt={m.name}
                  style={{
                    height: 220, width: 'auto', objectFit: 'contain', display: 'block', margin: 0, flexShrink: 0,
                    ...(m.image === 'qr' || !m.image ? { padding: '58px 16px', boxSizing: 'border-box' } : { padding: 0 }),
                  }}
                />
                <span style={{
                  fontSize: 9, fontWeight: 700, color: '#022040', textAlign: 'center',
                  whiteSpace: 'nowrap', zIndex: 3, lineHeight: 'normal',
                  margin: 0, padding: '2px 0 8px',
                }}>
                  {tag}
                </span>

                {/* Remove button */}
                <button
                  onClick={() => removeModule(m.uid)}
                  style={{
                    position: 'absolute', top: 4, right: 4,
                    width: 22, height: 22, borderRadius: '50%', border: 'none',
                    background: 'rgba(239,68,68,0.88)', color: '#fff',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 0, zIndex: 10, opacity: 0, transition: 'opacity 0.15s',
                  }}
                  className="vll-remove"
                  title="Eliminar"
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              );
            })}
          </div>

          {/* Height dimension */}
          {configLine.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 8px', minWidth: 38, flexShrink: 0 }}>
              <span style={S.arrowU} />
              <span style={S.lineV} />
              <span style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', fontSize: 10, fontWeight: 700, color: '#444', padding: '6px 0', fontFamily: 'Courier New, monospace', whiteSpace: 'nowrap' }}>
                {height}
              </span>
              <span style={S.lineV} />
              <span style={S.arrowD} />
            </div>
          )}
        </div>

        {configLine.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={() => push([])}
              style={{ padding: '4px 14px', fontSize: 12, border: '1.5px solid #e0e0e0', borderRadius: 6, background: '#fff', color: '#999', cursor: 'pointer', fontWeight: 600 }}
            >
              Limpiar linea
            </button>
          </div>
        )}
      </div>

      {/* ══ Sidebar catalog ══ */}
      <div style={{
        width: 230, flexShrink: 0,
        background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0',
        padding: 14, maxHeight: 520, overflowY: 'auto',
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#64748b', marginBottom: 10 }}>
          Modulos disponibles
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {catalog.map(prod => {
            const disabled = !!prod._disabled;
            const dims = getDims(prod.code);
            const isPosConflict = disabled && prod.category === 'pos' && (
              (hasQRInLine && !isQRProduct(prod)) || (hasNonQRInLine && isQRProduct(prod))
            );
            const disabledTitle = isPosConflict
              ? (isQRProduct(prod)
                  ? 'No se puede añadir QR junto con otros puntos de venta'
                  : 'El punto de venta QR no es compatible con otros puntos de venta')
              : 'No disponible para este entorno';
            // Short display name: strip "- Armario lockers xN" / env prefix for cleaner card
            const shortName = (prod.name || '')
              .replace(/^(INTERIOR|EXTERIOR)\s*-\s*/i, '')
              .replace(/\s*-\s*Armario lockers.*$/i, '');
            return (
            <div
              key={prod._id || prod.code}
              draggable={!disabled}
              onDragStart={(e) => !disabled && onCatalogDragStart(e, prod)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 8px', border: '1.5px solid #e2e8f0', borderRadius: 8,
                background: disabled ? '#f8f8f8' : '#fafbfc',
                cursor: disabled ? 'not-allowed' : 'grab',
                opacity: disabled ? 0.45 : 1,
                transition: 'border-color 0.15s, opacity 0.15s',
                position: 'relative',
              }}
              title={disabled ? disabledTitle : ''}
            >
              <img
                src={getModuleImage(prod, env === 'pos' ? (prod.env === 'outdoor' ? 'exterior' : 'interior') : env)}
                alt={prod.name}
                style={{ width: 38, height: 48, objectFit: 'contain', flexShrink: 0, filter: disabled ? 'grayscale(1)' : 'none' }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: disabled ? '#94a3b8' : '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {prod.code}
                </div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{prod.lockers} lockers</div>
                {dims && <div style={{ fontSize: 10, color: '#94a3b8' }}>{dims} <span style={{ fontSize: 9 }}>(por puerta)</span></div>}
              </div>
              {disabled ? (
                <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: 13 }}>🔒</span>
                </div>
              ) : (
              <button
                onClick={() => addModule(prod)}
                style={{
                  width: 26, height: 26, borderRadius: '50%', border: 'none',
                  background: '#022040', color: '#fff', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}
                title="Añadir modulo"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </button>
              )}
            </div>
            );
          })}
          {catalog.length === 0 && (
            <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic', padding: '12px 0' }}>
              Sin modulos para este entorno
            </div>
          )}
        </div>
      </div>

      {/* Hover CSS for module remove button */}
      <style>{`
        .vll-module:hover .vll-remove { opacity: 1 !important; }
        .vll-module:hover { transform: translateY(-3px); }
      `}</style>
    </div>
  );
}
